import type React from 'react'
import {useMemo, useState} from 'react'

import {useAppSelector} from '../../../../../../../../../../hooks/react-redux'
import {getEmptyHash} from '../../../../../../../../../../utils/empty'
import {getDraft} from '../../../../../../../EditPipelinePage/EditPipelinePage.selectors'
import {usePipelineId} from '../../../../../../../hooks/pipeline'
import {getValidationErrorMessage} from '../../../../../../../utils/validators'

import {parameterNameValidators, parameterValueValidators} from './usePipelineParameterForm.const'

function usePipelineParameterForm(
  parameterName: string,
  parameterValue: string,
  onConfirm: (name: string, value: string) => void,
) {
  const pipelineId = usePipelineId()
  const [fieldName, setFieldName] = useState<string>(parameterName)
  const [fieldValue, setFieldValue] = useState<string>(parameterValue)
  const [parameterNameErrorMessage, setParameterNameErrorMessage] = useState<string | null>()
  const [parameterValueErrorMessage, setParameterValueErrorMessage] = useState<string | null>()
  const parameters: Record<string, string | undefined> = useAppSelector(
    state => getDraft(state, pipelineId)?.settings.parameters!,
  )
  const secrets: Record<string, string | undefined> = useAppSelector(
    state => getDraft(state, pipelineId)?.settings.secrets ?? getEmptyHash(),
  )
  const compareParamsArray = useMemo(
    () => Object.keys(parameters).filter(key => key !== parameterName),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parameters],
  )
  const parameterNameChangeInputHandler = ({target}: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = target
    const {message} = getValidationErrorMessage({
      value,
      validators: parameterNameValidators,
      compareObject: secrets,
      compareArray: compareParamsArray,
    })

    if (message) {
      setParameterNameErrorMessage(message)
    } else {
      setParameterNameErrorMessage(null)
    }

    setFieldName(value)
  }
  const parameterValueChangeInputHandler = ({target}: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = target
    const {message} = getValidationErrorMessage({
      value,
      validators: parameterValueValidators,
    })

    if (message) {
      setParameterValueErrorMessage(message)
    } else {
      setParameterValueErrorMessage(null)
    }

    setFieldValue(value)
  }
  const confirmClickHandler = () => onConfirm(fieldName, fieldValue)
  const isFormInvalid = Boolean(
    parameterNameErrorMessage || parameterValueErrorMessage || !fieldName || !fieldValue,
  )

  return {
    fieldName,
    fieldValue,
    isFormInvalid,
    parameterNameErrorMessage,
    parameterValueErrorMessage,
    parameterNameChangeInputHandler,
    parameterValueChangeInputHandler,
    confirmClickHandler,
  }
}

export default usePipelineParameterForm
